import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTableModule, MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UploadAttachment } from 'src/app/shared/components/upload-attachments/upload-attachment.model';
import { ComplianceRequestType } from 'src/app/shared/models/enums/compliance-request-type.enum';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import {
    ComplianceActionService,
    ActionType
} from 'src/app/compliance/compliance-requests/compliance-details-fly-out/compliance-action.service';

@Component({
    selector: 'nova-upload-attachments',
    templateUrl: './upload-attachments.component.html',
    styleUrls: ['./upload-attachments.component.scss'],
    standalone: true,
    imports: [MatLegacyTableModule, MatIconModule, MatTooltipModule, MatLegacyButtonModule]
})
export class UploadAttachmentsComponent implements OnInit, OnChanges {
    private readonly maxFileSize = 50 * 1000000;

    @Input()
    attachments: UploadAttachment[] = [];

    @Input()
    editModeFlag?: boolean = true;

    @Input()
    isTitleClickable?: boolean = true;

    @Input()
    fileColumnNeeded = false;

    @Input()
    isTitleEditable = false;

    @Input()
    qaComponentSuffix = '';

    @Input()
    requestType?: ComplianceRequestType = ComplianceRequestType.ShippingRequest;

    @Input()
    useFileDeletionConfirmDialog = false;

    @ViewChild('fileInput', { static: false })
    fileInput: ElementRef;

    dataSource = new MatTableDataSource<UploadAttachment>();
    isDragging: boolean;
    definedColumns: string[];

    validationMessage = '';

    @Output()
    selectedFiles = new EventEmitter<UploadAttachment[]>();

    @Output()
    removedFile = new EventEmitter<UploadAttachment>();

    @Output()
    clickedFile = new EventEmitter<UploadAttachment>();

    constructor(
        private readonly _dialogService: ConfirmDialogService,
        private readonly _actionService: ComplianceActionService
    ) {}

    ngOnInit(): void {
        if (this.fileColumnNeeded) {
            this.definedColumns = ['title', 'file', 'delete'];
        } else {
            if (this.editModeFlag === false && this.requestType === ComplianceRequestType.VendorAdmin) {
                this.definedColumns = ['title'];
            } else {
                this.definedColumns = ['title', 'delete'];
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.attachments) {
            if (Array.isArray(this.attachments)) {
                this.dataSource.data = this.attachments.slice();
            }
        }
    }

    onDragOver($event: DragEvent): void {
        $event.stopPropagation();
        $event.preventDefault();

        this.isDragging = true;
    }

    onDragLeave($event: DragEvent): void {
        $event.stopPropagation();
        $event.preventDefault();

        this.isDragging = false;
    }

    onDrop($event: DragEvent): void {
        $event.preventDefault();
        $event.stopPropagation();

        this.isDragging = false;
        this.addFiles($event.dataTransfer.files);
    }

    addSelectedFiles() {
        this.addFiles(this.fileInput.nativeElement.files);
        this.fileInput.nativeElement.value = null;
    }

    onClickAttachment(element: UploadAttachment): void {
        const file = this.dataSource.data.find((item) => item.id === element.id);
        if (file) {
            this.clickedFile.emit(file);
        }
    }

    private addFiles(value: FileList) {
        const files: UploadAttachment[] = Array.from(value).map((file) => {
            return {
                title: file.name,
                file,
                pending: true,
                error: this.validateFile(file)
            };
        });

        this.dataSource.data = [...this.dataSource.data, ...files];

        this.updateValidationMessage();

        this.selectedFiles.emit(files);
    }

    private validateFile(file: File): null | string {
        if (file.size > this.maxFileSize) {
            return 'maxSize';
        }

        return null;
    }

    updateValidationMessage(): void {
        this.validationMessage = '';

        const oversizeFiles = this.dataSource.data.filter((f) => f.error === 'maxSize').map((f) => f.title);

        if (oversizeFiles.length > 0) {
            const filesLength = oversizeFiles.length;
            const fileList = filesLength === 2 ? oversizeFiles.join(' & ') : oversizeFiles.join(', ');

            this.validationMessage = `Oops! Your file${filesLength === 1 ? '' : 's'}, ${fileList}, ${
                filesLength === 1 ? 'was' : 'were'
            } too large to add. 50MB max.`;
        }
    }

    private _removeFile(file: UploadAttachment, index: number) {
        this.dataSource.data = this.dataSource.data.filter((x, i) => i !== index);
        if (this.dataSource.data.length === 0) {
            this.dataSource = new MatTableDataSource<UploadAttachment>();
            this.fileInput.nativeElement.value = null;
        }
        this.updateValidationMessage();

        if (!file.pending) {
            this.removedFile.emit(file);
        } else {
            this._actionService.setAction({
                action: ActionType.AttachmentsChanged,
                data: file
            });
        }
    }

    onRemoveFile(index: number): void {
        const file = this.dataSource.data[index];

        if (this.useFileDeletionConfirmDialog && !file.pending) {
            this._dialogService
                .showDialog({
                    message: `This entry will be deleted and the action cannot be undone.`,
                    action: 'YES, DELETE',
                    title: 'Are You Sure You Want To Delete?',
                    needCancel: true,
                    cancelText: 'CANCEL',
                    needExtraPadding: false
                })
                .subscribe((confirmed) => {
                    if (confirmed) {
                        this._removeFile(file, index);
                    }
                });
        } else {
            this._removeFile(file, index);
        }
    }

    get pendingFiles() {
        return this.dataSource.data.filter((file) => file.pending);
    }

    browse() {
        this.fileInput.nativeElement.click();
    }

    onTitleChange($event, idx: number): void {
        this.dataSource.data[idx].title = $event.target.value;
    }
}
